import { TextField, Box, IconButton } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { useState, useContext } from "react";

import { ChatContext } from "../../contexts/chat/context";
import { CHAT_FONT_STACK } from "../../contexts/material_ui/theme";

export const InputContainer = () => {
  const {
    sendMessage,
    stopCurrentResponse,
    isAwaitingResponse,
    agentState,
    isStopRequested,
  } = useContext(ChatContext);
  const [inputValue, setInputValue] = useState("");

  // Allow sending while busy; backend will queue safely.
  const isBusy = isAwaitingResponse || agentState.status === "streaming";
  const canSend = !!inputValue.trim();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = () => {
    if (inputValue.trim()) {
      sendMessage(inputValue);
      setInputValue("");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: 1,
        width: "100%",
        py: 0.75,
        px: 1.25,
        backgroundColor: "#ffffff",
        borderRadius: "24px",
        border: "2px solid",
        borderColor: "grey.200",
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        transition: "all 0.2s ease",
        "&:focus-within": {
          borderColor: "#1976d2",
          boxShadow: "0 4px 20px rgba(25, 118, 210, 0.15)",
        },
      }}
    >
      <TextField
        fullWidth
        multiline
        maxRows={4}
        placeholder={isBusy ? "Ask while Nucleus is working (it will queue)..." : "ask Nucleus ..."}
        value={inputValue}
        onChange={handleInputChange}
        onKeyPress={handleKeyPress}
        variant="standard"
        size="small"
        disabled={false}
        sx={{
          "& .MuiInput-root": {
            alignItems: "flex-start",
            fontSize: "0.95rem",
            fontFamily: CHAT_FONT_STACK,
            "&:before, &:after": {
              display: "none",
            },
            "&.Mui-disabled": {
              color: "text.disabled",
            },
          },
          "& .MuiInputBase-input": {
            padding: "4px 4px",
            fontFamily: CHAT_FONT_STACK,
            "&::placeholder": {
              color: "text.secondary",
              opacity: 0.7,
            },
          },
        }}
      />
      <IconButton
        onClick={handleSendMessage}
        disabled={!canSend}
        sx={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: "50%",
          backgroundColor: canSend ? "#1976d2" : "#e8e8e8",
          color: "white",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: canSend ? "#1565c0" : "#e8e8e8",
            transform: canSend ? "scale(1.08)" : "none",
          },
          "&.Mui-disabled": {
            backgroundColor: "#e8e8e8",
            color: "#bdbdbd",
          },
        }}
      >
        <SendIcon sx={{ fontSize: 18 }} />
      </IconButton>
      {isBusy && (
        <IconButton
          onClick={() => stopCurrentResponse(false)}
          disabled={isStopRequested}
          sx={{
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: "#fff4f4",
            color: "#c62828",
            border: "1px solid #ef9a9a",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "#ffeaea",
              transform: isStopRequested ? "none" : "scale(1.05)",
            },
            "&.Mui-disabled": {
              backgroundColor: "#f5f5f5",
              color: "#bdbdbd",
              borderColor: "#e0e0e0",
            },
          }}
        >
          <StopCircleOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  );
};
